import { useEffect, useState, useCallback } from "react";
import { Header } from "@/components/Header";
import { StaffCard } from "@/components/StaffCard";
import { StaffDetailModal } from "@/components/StaffDetailModal";
import { BirthdaysWidget } from "@/components/BirthdaysWidget";
import { StaffFormDialog } from "@/components/StaffFormDialog";
import { ImportDialog } from "@/components/ImportDialog";
import { RosterImportDialog } from "@/components/RosterImportDialog";
import { YearSelector } from "@/components/YearSelector";
import { useAuth } from "@/context/AuthContext";
import { getStaff, getDepartments, getBirthdays, getYears } from "@/lib/api";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Plus, Table2, Database, RefreshCw, Loader2 } from "lucide-react";

export default function Directory() {
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [level, setLevel] = useState("all");
  const [sort, setSort] = useState("last_asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(() => localStorage.getItem("nefa_year") || "");

  const refreshYears = useCallback(async () => {
    const ys = await getYears();
    setYears(ys);
    setYear((cur) => {
      if (cur && ys.find((y) => y.label === cur)) return cur;
      const active = ys.find((y) => y.active) || ys[0];
      return active ? active.label : "";
    });
  }, []);

  useEffect(() => { refreshYears(); }, [refreshYears]);
  useEffect(() => { if (year) localStorage.setItem("nefa_year", year); }, [year]);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    const [s, d, b] = await Promise.all([
      getStaff({ search: search || undefined, department: dept, year }),
      getDepartments(),
      getBirthdays(year),
    ]);
    setStaff(s);
    setDepartments(d);
    setBirthdays(b);
    setLoading(false);
  }, [search, dept, year]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const afterImport = () => { load(); refreshYears(); };

  const currentYear = years.find((y) => y.label === year);
  const linkedSheet = currentYear?.sheet_url || "";

  const filteredStaff = level === "all"
    ? staff
    : staff.filter((s) => (s.level || "").toLowerCase().includes(level));

  const byText = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
  const sortComparators = {
    last_asc: (a, b) => byText(a.last_name || "", b.last_name || "") || byText(a.first_name || "", b.first_name || ""),
    last_desc: (a, b) => byText(b.last_name || "", a.last_name || "") || byText(b.first_name || "", a.first_name || ""),
    campus_asc: (a, b) => byText(a.school_campus || "", b.school_campus || "") || byText(a.last_name || "", b.last_name || ""),
  };
  const visibleStaff = [...filteredStaff].sort(sortComparators[sort] || sortComparators.last_asc);

  const doSync = async () => {
    if (!year || syncing || !linkedSheet) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/import/neisd/commit", {}, { params: { year } });
      const parts = [`${data.inserted} added`, `${data.updated} updated`];
      if (data.removed) parts.push(`${data.removed} removed`);
      toast.success(`${year} synced from response sheet: ${parts.join(", ")}`);
      afterImport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed. Check the response sheet sharing settings.");
    } finally { setSyncing(false); }
  };

  return (
    <div className="min-h-screen bg-[#F3F5F8]">
      <Header>
        <YearSelector years={years} current={year} onSelect={setYear} onChanged={refreshYears} isAdmin={isAdmin} />
        {isAdmin && (
          <>
            <Button
              data-testid="sync-responses-btn"
              onClick={doSync}
              disabled={syncing || !year || !linkedSheet}
              className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] gap-2 uppercase text-xs tracking-wider disabled:opacity-50"
              title={linkedSheet ? "Pull the latest form responses into this school year" : "Link a sheet first via Import Roster"}
            >
              {syncing
                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                : <RefreshCw className="w-4 h-4" strokeWidth={1.5} />}
              {syncing ? "Syncing…" : "Sync Latest Responses"}
            </Button>
            <Button
              data-testid="import-roster-btn"
              onClick={() => setRosterOpen(true)}
              className="rounded-none bg-[#C0982E] text-[#F3F5F8] hover:bg-[#0A1F44] gap-2 uppercase text-xs tracking-wider hidden lg:inline-flex"
            >
              <Database className="w-4 h-4" strokeWidth={1.5} /> Import Roster
            </Button>
            <Button
              data-testid="import-sheet-btn"
              onClick={() => setImportOpen(true)}
              variant="outline"
              className="rounded-none border-[#0A1F44] text-[#0A1F44] hover:bg-[#E6EAF1] gap-2 uppercase text-xs tracking-wider hidden lg:inline-flex"
            >
              <Table2 className="w-4 h-4" strokeWidth={1.5} /> Import Sheet
            </Button>
            <Button
              data-testid="add-staff-btn"
              onClick={() => setDialogOpen(true)}
              className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] gap-2 uppercase text-xs tracking-wider hidden sm:inline-flex"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> Add Staff
            </Button>
          </>
        )}
      </Header>

      <section className="px-5 sm:px-8 pt-12 pb-8 max-w-[1400px] mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C0982E] mb-3" data-testid="hero-year">
          {year ? `${year} Faculty` : "Faculty"}
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-[#0A1F44] max-w-3xl">
          eContact
        </h1>
        <div className="mt-2 text-xs text-[#0A1F44]/50" data-testid="hero-credit">
          Created by Joseph Johnson · &copy; 2026
        </div>
        <div className="mt-6 max-w-2xl">
          <BirthdaysWidget birthdays={birthdays} />
        </div>
        <p className="mt-6 text-[#0A1F44]/60 max-w-xl">
          Search {staff.length ? "" : ""}the full staff directory, browse by program,
          and find contact info, room numbers &amp; daily schedules.
        </p>
      </section>

      <div className="px-5 sm:px-8 max-w-[1400px] mx-auto pb-20">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F44]/40" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              placeholder="Search by name, email, room…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none border-[#0A1F44]/30 bg-transparent pl-9 focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 shrink-0">Program</span>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger data-testid="dept-filter-select" className="rounded-none border-[#0A1F44]/30 bg-transparent w-56 focus:ring-1 focus:ring-[#C0982E]">
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="dept-option-all" className="text-[#0A1F44] focus:bg-[#C0982E] focus:text-[#0A1F44]">All Programs</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d} data-testid={`dept-option-${d}`} className="text-[#0A1F44] focus:bg-[#C0982E] focus:text-[#0A1F44]">{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 shrink-0">Campus</span>
            <div data-testid="level-filter" className="flex gap-0 border-l border-t border-[#0A1F44]/20 w-fit">
              {[["all", "All"], ["high", "High School"], ["middle", "Middle School"]].map(([val, label]) => (
                <button
                  key={val}
                  data-testid={`level-filter-${val}`}
                  onClick={() => setLevel(val)}
                  className={`border-r border-b border-[#0A1F44]/20 px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                    level === val
                      ? "bg-[#0A1F44] text-[#F3F5F8]"
                      : "text-[#0A1F44]/70 hover:bg-[#E6EAF1]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 shrink-0">Sort by</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="rounded-none border-[#0A1F44]/30 bg-transparent w-52 focus:ring-1 focus:ring-[#C0982E]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="last_asc" data-testid="sort-option-last_asc" className="text-[#0A1F44] focus:bg-[#C0982E] focus:text-[#0A1F44]">Last Name (A–Z)</SelectItem>
                <SelectItem value="last_desc" data-testid="sort-option-last_desc" className="text-[#0A1F44] focus:bg-[#C0982E] focus:text-[#0A1F44]">Last Name (Z–A)</SelectItem>
                <SelectItem value="campus_asc" data-testid="sort-option-campus_asc" className="text-[#0A1F44] focus:bg-[#C0982E] focus:text-[#0A1F44]">Campus Name (A–Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-8">
          <div className="border border-[#0A1F44]/20 p-6 flex gap-10 items-start w-fit">
            <div>
              <div className="font-serif text-4xl text-[#0A1F44]" data-testid="total-count">
                {visibleStaff.length}
              </div>
              <div className="text-sm text-[#0A1F44]/60">staff shown</div>
            </div>
            <div>
              <div className="font-serif text-4xl text-[#0A1F44]">{departments.length}</div>
              <div className="text-sm text-[#0A1F44]/60">programs</div>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-[#0A1F44]/50 py-10" data-testid="loading-state">Loading…</p>
        ) : visibleStaff.length === 0 ? (
          <p className="text-[#0A1F44]/50 py-10" data-testid="empty-state">No staff found.</p>
        ) : (
          <div
            data-testid="staff-grid"
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-0 border-l border-t border-[#0A1F44]/20"
          >
            {visibleStaff.map((s) => (
              <StaffCard key={s.id} staff={s} onSelect={setSelected} />
            ))}
          </div>
        )}
      </div>

      <StaffFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={afterImport} year={year} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={afterImport} year={year} />
      <RosterImportDialog open={rosterOpen} onOpenChange={setRosterOpen} onImported={afterImport} year={year} linkedSheet={linkedSheet} />
      <StaffDetailModal
        staff={selected}
        open={!!selected}
        onOpenChange={(v) => { if (!v) setSelected(null); }}
        onChanged={load}
      />
    </div>
  );
}
